import Button from '@/components/basicComponents/button';
import Input from '@/components/basicComponents/input';
import Select from '@/components/basicComponents/select';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { zodResolver } from '@hookform/resolvers/zod';
import { COUNTRY_CALLING_CODES_MAP, userMobileNumberInputSchema } from '@trace-8/trace8-schema';
import { multiFactor, PhoneMultiFactorInfo } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { TwoFactorAuthenticationDescription, TwoFactorAuthenticationTitle } from '..';

interface IForm {
  mobileNumber: {
    countryCode: string;
    number: string;
  };
}

const defaultFormValues: IForm = {
  mobileNumber: {
    countryCode: undefined,
    number: undefined
  }
};

interface AddMobilePhoneNumberProps {
  mobileNumber: { countryCode: string; number: string };
  handleSignOut: () => void;
  openModalWindow: (type: string, mobileNumber: { countryCode: string; number: string }) => void;
}

export const AddMobilePhoneNumber = ({ mobileNumber, handleSignOut, openModalWindow }: AddMobilePhoneNumberProps) => {
  const { user, auth } = useAuth();
  const { isMobile } = useResponsive();
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
    setError
  } = useForm<IForm>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(userMobileNumberInputSchema) as Resolver<IForm>
  });

  const [tenantName, setTenantName] = useState<string>('');

  useEffect(() => {
    if (mobileNumber?.countryCode && mobileNumber?.number) {
      reset({ mobileNumber });
    }
  }, [mobileNumber]);

  useEffect(() => {
    if (!user) return;
    setTenantName(user?.tenant?.name ?? '');
  }, [user]);

  const onSave = (data: IForm) => {
    const currentUser = auth?.currentUser;
    const multiFactorUser = multiFactor(currentUser);
    const multiFactorPhone = multiFactorUser.enrolledFactors.find((factor) => factor.factorId === 'phone') as PhoneMultiFactorInfo | undefined;
    if (multiFactorUser && multiFactorPhone) {
      if (multiFactorPhone && multiFactorPhone.phoneNumber === `+${data.mobileNumber.countryCode}${data.mobileNumber.number}`) {
        setError('mobileNumber.number', { message: 'This phone number is already enrolled for this user.' });
        return;
      } else {
        openModalWindow('verification-required', data.mobileNumber);
        return;
      }
    }
    openModalWindow('verification-required', data.mobileNumber);
  };

  return (
    <>
      <TwoFactorAuthentication>
        <VerifyPhoneImage src="/static/mock-images/verifyPhone.webp" alt="verify phone image" />
        <TwoFactorAuthenticationTitle>Add a Mobile Phone Number</TwoFactorAuthenticationTitle>
        <PhoneFields>
          <Controller
            name="mobileNumber.countryCode"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                label="Country Code"
                value={value || ''}
                setValue={onChange}
                options={Object.entries(COUNTRY_CALLING_CODES_MAP).map(([key, value]) => ({ label: value, value: key }))}
                errorText={error?.message}
                size={isMobile ? 'md' : 'lg'}
                width="200px"
              />
            )}
          />
          <Controller
            name="mobileNumber.number"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                label="Mobile Number"
                value={value || ''}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                errorText={error?.message}
                size={isMobile ? 'md' : 'lg'}
              />
            )}
          />
        </PhoneFields>
        <TwoFactorAuthenticationDescription>
          <p>
            Two factor authentication is required to access {tenantName}. Provide a phone number for{' '}
            <span style={{ fontWeight: 600 }}>{user?.email ?? ''}</span> to continue.
          </p>
        </TwoFactorAuthenticationDescription>
        <ButtonsWrapper>
          <Button size="md" onClick={handleSubmit(onSave)} styleType={!!Object.keys(errors || {}).length ? 'disabled' : 'default'}>
            Continue
          </Button>
          <Button size="md" onClick={handleSignOut} styleType="outline">
            Sign Out
          </Button>
        </ButtonsWrapper>
        <Divider />
        <TwoFactorAuthenticationDescription>
          <WeakDescription>
            By entering your mobile phone number, you consent to receive automated security notifications via text message from {tenantName}. Your
            mobile phone number will not be shared with third parties or affiliates for marketing or promotional purposes without your permission.
            Message and data rates amy apply. Message frequency varies.
          </WeakDescription>
        </TwoFactorAuthenticationDescription>
      </TwoFactorAuthentication>
    </>
  );
};

const TwoFactorAuthentication = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  max-width: 420px;
  width: 100%;
  padding: 0 20px 30px 20px;
`;

const VerifyPhoneImage = styled.img`
  margin: 0 auto;
  width: 143px;
  height: 146px;
`;

const PhoneFields = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  padding-top: 20px;
`;

const WeakDescription = styled.p`
  color: ${themeVar('font.weak')};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin: 20px 0;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${themeVar('border.base')};
`;
